import UINumber from 'components/UI/Number/Number';
import UICurrency from 'components/UI/Currency/Currency';
import Tippy from '@tippyjs/react';
import {
  PlacementProps,
  RenderLabelProps,
  ShouldRenderType,
} from 'components/Placement/types';

import './StatsTable.scss';

const cbn = 'placement-view-stats-table';

type ReportingWindow =
  | 'premiere'
  | 'past30'
  | 'past60'
  | 'past90'
  | 'allTime'
  | 'past365';

const getMediaValueReportingWindowLabel = (
  reportingWindow: ReportingWindow,
): string | null => {
  switch (reportingWindow) {
    case 'premiere':
      return 'Premiere';
    case 'past30':
      return '30 days';
    case 'past60':
      return '60 days';
    case 'past90':
      return '90 days';
    case 'allTime':
    case 'past365':
      return '365 days';
    default:
      return null;
  }
};

const MEDIA_VALUE_KEY = 'qualitySection.statsTable.mediaValue';

type PlacementViewStatsTableProps = {
  channelName: string;
  durationLength: number;
  mediaValues: PlacementProps['mediaValues'];
  placedBy?: string;
  method?: string;
  reportingWindow: ReportingWindow;
  cpm: number | null;
  renderLabel: RenderLabelProps;
  shouldRender: ShouldRenderType;
  verbalMentions: number;
};

function PlacementViewStatsTable({
  channelName,
  durationLength,
  mediaValues,
  placedBy,
  method,
  reportingWindow: originalReportingWindow,
  cpm,
  renderLabel,
  shouldRender,
  verbalMentions,
}: Readonly<PlacementViewStatsTableProps>) {
  // if there is no media value for the reporting window (past 30 by default), use premiere
  const shouldReplaceWithPremiere =
    (mediaValues?.[originalReportingWindow] ?? 0) === 0;

  const reportingWindow: ReportingWindow = shouldReplaceWithPremiere
    ? 'premiere'
    : originalReportingWindow;

  const mediaValueReportingWindowLabel = getMediaValueReportingWindowLabel(
    reportingWindow,
  );

  return (
    <div className={cbn}>
      <div className={`${cbn}__row`}>
        {shouldRender('qualitySection.statsTable.duration') && (
          <div className={`${cbn}__col`}>
            <span className={`${cbn}__col__label`}>
              {renderLabel('qualitySection.statsTable.duration', 'Duration')}
            </span>
            <UINumber format="00:00">{durationLength}</UINumber>
          </div>
        )}

        {shouldRender('qualitySection.statsTable.verbals') && (
          <div className={`${cbn}__col`}>
            <span className={`${cbn}__col__label`}>
              {renderLabel('qualitySection.statsTable.verbals', 'Verbals')}
            </span>
            {verbalMentions || 0}
          </div>
        )}

        {shouldRender('qualitySection.statsTable.type') && (
          <div className={`${cbn}__col`}>
            <span className={`${cbn}__col__label`}>
              {renderLabel('qualitySection.statsTable.type', 'Type')}
            </span>
            {/* eslint-disable-next-line no-nested-ternary */}
            {placedBy === 'BEN'
              ? method
              : placedBy === 'Unknown'
              ? 'Not Placed By BEN'
              : null}
          </div>
        )}

        {['film', 'tv'].includes(channelName) &&
          cpm !== null &&
          cpm > 0 &&
          shouldRender('qualitySection.statsTable.cpm') && (
            <div className={`${cbn}__col`}>
              <span className={`${cbn}__col__label`}>
                {renderLabel('qualitySection.statsTable.cpm', 'Earned CPM')}
              </span>
              <UICurrency format="$0,0[.]00">{cpm}</UICurrency>
            </div>
          )}

        {['film', 'tv'].includes(channelName) &&
          mediaValues?.[reportingWindow] > 0 &&
          shouldRender(MEDIA_VALUE_KEY) && (
            <div className={`${cbn}__col`}>
              {!shouldReplaceWithPremiere ? (
                <span className={`${cbn}__col__label`}>
                  {renderLabel(MEDIA_VALUE_KEY, 'Media Value')}
                  {mediaValueReportingWindowLabel &&
                    ` (${mediaValueReportingWindowLabel})`}
                </span>
              ) : (
                <Tippy
                  content="Showing Premiere until 30 days Media Value is available"
                  placement="top"
                  arrow
                  delay={100}
                  animation="shift-toward"
                  appendTo={document.body}
                >
                  <span className={`${cbn}__col__label`}>
                    {renderLabel(MEDIA_VALUE_KEY, 'Media Value')}
                    {` (${mediaValueReportingWindowLabel}) *`}
                  </span>
                </Tippy>
              )}

              <UICurrency format="$0,0[.]00">
                {mediaValues?.[reportingWindow]}
              </UICurrency>
            </div>
          )}
      </div>
    </div>
  );
}

export default PlacementViewStatsTable;
